import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, AlertCircle, CheckCircle2, User, Phone, Mail,
  MessageSquare, Clock,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

interface SupportTicket {
  id: string;
  number: string;
  subject: string;
  body?: string;
  status: 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  channel: string;
  customerName?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  assignedTo?: { id: string; firstName: string; lastName: string } | null;
  resolution?: string | null;
  firstResponseAt?: string | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  slaDeadline?: string | null;
  createdAt: string;
}

const STATUS_OPTIONS: { value: SupportTicket['status']; label: string; color: string }[] = [
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300' },
  { value: 'waiting_customer', label: 'Waiting Customer', color: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300' },
  { value: 'resolved', label: 'Resolved', color: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300' },
  { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400' },
];

export function SupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resolution, setResolution] = useState('');

  const fetch = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/support/tickets/${id}`);
      const t: SupportTicket = res.data.data?.ticket ?? res.data.data ?? res.data;
      setTicket(t);
      setResolution(t.resolution || '');
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to load ticket';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  const updateStatus = async (status: SupportTicket['status']) => {
    if (!id) return;
    setSaving(true);
    try {
      await api.patch(`/support/tickets/${id}`, { status });
      fetch();
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Could not update status';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const saveResolution = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await api.patch(`/support/tickets/${id}`, { resolution });
      fetch();
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Could not save';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="mx-auto max-w-xl rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <AlertCircle size={18} className="mb-2" />
        {error || 'Ticket not found'}
      </div>
    );
  }

  const current = STATUS_OPTIONS.find(s => s.value === ticket.status);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/support/tickets')}
          className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="font-mono">{ticket.number}</span>
            <span>·</span>
            <span className="capitalize">{ticket.channel}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{ticket.subject}</h1>
        </div>
        {current && (
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${current.color}`}>
            {current.label}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Main */}
        <div className="space-y-4 lg:col-span-2">
          <Panel title="Description">
            <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
              {ticket.body || <span className="text-gray-400">No description provided.</span>}
            </p>
          </Panel>

          <Panel title="Resolution">
            <textarea
              rows={4}
              value={resolution}
              onChange={e => setResolution(e.target.value)}
              placeholder="How was this resolved? (customer-facing)"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
            <div className="mt-2 flex justify-end">
              <button
                onClick={saveResolution}
                disabled={saving}
                className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:bg-gray-400"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Save resolution
              </button>
            </div>
          </Panel>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Panel title="Status">
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map(s => (
                <button
                  key={s.value}
                  onClick={() => updateStatus(s.value)}
                  disabled={saving || ticket.status === s.value}
                  className={`rounded-full border px-2 py-1 text-xs font-medium transition-colors ${
                    ticket.status === s.value
                      ? `${s.color} border-transparent cursor-default`
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </Panel>

          <Panel title="Customer">
            {ticket.customerName ? (
              <div className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                <User size={14} className="text-gray-400" />
                {ticket.customerName}
              </div>
            ) : null}
            {ticket.customerPhone && (
              <a href={`tel:${ticket.customerPhone}`} className="mt-1 flex items-center gap-2 text-sm text-[var(--ink-0)] hover:underline">
                <Phone size={14} /> {ticket.customerPhone}
              </a>
            )}
            {ticket.customerEmail && (
              <a href={`mailto:${ticket.customerEmail}`} className="mt-1 flex items-center gap-2 text-sm text-[var(--ink-0)] hover:underline">
                <Mail size={14} /> {ticket.customerEmail}
              </a>
            )}
            {!ticket.customerName && !ticket.customerPhone && !ticket.customerEmail && (
              <div className="text-xs text-gray-400">No customer info</div>
            )}
          </Panel>

          <Panel title="Timeline">
            <TimelineRow icon={<MessageSquare size={12} />} label="Created" at={ticket.createdAt} />
            {ticket.firstResponseAt && <TimelineRow icon={<Clock size={12} />} label="First response" at={ticket.firstResponseAt} />}
            {ticket.resolvedAt && <TimelineRow icon={<CheckCircle2 size={12} />} label="Resolved" at={ticket.resolvedAt} />}
            {ticket.closedAt && <TimelineRow icon={<CheckCircle2 size={12} />} label="Closed" at={ticket.closedAt} />}
            {ticket.slaDeadline && (
              <TimelineRow icon={<Clock size={12} />} label="SLA deadline" at={ticket.slaDeadline} warn />
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h3>
      {children}
    </section>
  );
}

function TimelineRow({ icon, label, at, warn }: { icon: React.ReactNode; label: string; at: string; warn?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 py-1.5 text-xs last:border-0 dark:border-gray-800">
      <span className={`inline-flex items-center gap-1.5 ${warn ? 'text-amber-700 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>
        {icon} {label}
      </span>
      <span className={warn ? 'text-amber-700 font-medium dark:text-amber-400' : 'text-gray-500'}>
        {formatDate(at)}
      </span>
    </div>
  );
}
